import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'

// Floating "Ask AI" button + slide-out chat panel, mounted once in App.jsx so
// it appears on every tab. Talks to POST /api/ask (Claude + query tools).
// Design mirrors the approved mockup (askai-mockup artifact).

const PAGE_LABELS = {
    '/': 'Production', '/production': 'Production', '/leeview': 'LEEVIEW',
    '/map': 'Map', '/financials': 'Financials', '/workovers': 'Workovers',
    '/tanks': 'Sales/Tank', '/well-analysis': 'Well Analysis',
    '/well-review': 'Well Review', '/hedge': 'Hedge', '/downloads': 'Downloads',
}

const STORAGE_KEY = 'askai_messages'

const SparkleIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M12 2l1.9 5.6L19.5 9.5l-5.6 1.9L12 17l-1.9-5.6L4.5 9.5l5.6-1.9L12 2zm7 11l.95 2.8 2.8.95-2.8.95L19 20.5l-.95-2.8-2.8-.95 2.8-.95L19 13zM5 14l.8 2.35 2.35.75-2.35.8L5 20.25l-.8-2.35-2.35-.8 2.35-.75L5 14z" />
    </svg>
)

// Minimal renderer: paragraphs, **bold**, and pipe tables (no markdown lib).
function renderInline(text, keyPrefix) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
        part.startsWith('**') && part.endsWith('**')
            ? <strong key={`${keyPrefix}-${j}`}>{part.slice(2, -2)}</strong>
            : part.split('\n').map((line, k, arr) => (
                <span key={`${keyPrefix}-${j}-${k}`}>{line}{k < arr.length - 1 && <br />}</span>
            ))
    )
}

function renderAnswer(text) {
    return text.split(/\n{2,}/).map((block, i) => {
        const lines = block.split('\n').filter(l => l.trim())
        const isTable = lines.length >= 2 && lines.every(l => l.trim().startsWith('|'))
        if (isTable) {
            const rows = lines
                .filter(l => !/^\s*\|[\s\-:|]+\|\s*$/.test(l)) // drop |---|---| separator
                .map(l => l.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim()))
            const [head, ...body] = rows
            return (
                <div key={i} className={`${i > 0 ? 'mt-2' : ''} overflow-x-auto`}>
                    <table className="w-full text-xs tabular-nums">
                        <thead>
                            <tr className="opacity-60">
                                {head.map((c, j) => <th key={j} className="px-2 py-1 text-right first:text-left font-semibold whitespace-nowrap">{renderInline(c, `h${j}`)}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {body.map((r, j) => (
                                <tr key={j} className="border-t border-current/10">
                                    {r.map((c, k) => <td key={k} className="px-2 py-0.5 text-right first:text-left whitespace-nowrap">{renderInline(c, `b${j}-${k}`)}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )
        }
        return <p key={i} className={i > 0 ? 'mt-2' : ''}>{renderInline(block, `p${i}`)}</p>
    })
}

function DataTable({ data, dark }) {
    if (!data?.rows?.length) return null
    const cols = data.columns || Object.keys(data.rows[0])
    const keys = Object.keys(data.rows[0])
    return (
        <div className={`mt-2 max-h-48 overflow-auto rounded border ${dark ? 'border-slate-700' : 'border-gray-200'}`}>
            <table className="w-full text-xs tabular-nums">
                <thead>
                    <tr className={dark ? 'bg-slate-800 text-gray-400' : 'bg-gray-100 text-gray-500'}>
                        {cols.map(c => <th key={c} className="px-2 py-1 text-right first:text-left font-semibold whitespace-nowrap">{c}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {data.rows.map((r, i) => (
                        <tr key={i} className={dark ? 'border-t border-slate-800' : 'border-t border-gray-100'}>
                            {keys.map(k => (
                                <td key={k} className="px-2 py-0.5 text-right first:text-left whitespace-nowrap">{r[k] ?? ''}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default function AskAI() {
    const { theme } = useTheme()
    const dark = theme === 'dark'
    const location = useLocation()

    const [open, setOpen] = useState(false)
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const [messages, setMessages] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || [] } catch { return [] }
    })
    const scrollRef = useRef(null)
    const inputRef = useRef(null)
    const abortRef = useRef(null)

    const params = new URLSearchParams(location.search)
    const combo = params.get('combo') || undefined
    const well = params.get('well') || undefined
    const page = PAGE_LABELS[location.pathname] ||
        PAGE_LABELS['/' + location.pathname.split('/')[1]] || undefined

    useEffect(() => {
        try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-20))) } catch { /* full */ }
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
    }, [messages])

    useEffect(() => {
        if (open) inputRef.current?.focus()
        const onKey = e => { if (e.key === 'Escape') setOpen(false) }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    const send = async (text) => {
        const question = (text ?? input).trim()
        if (!question || loading) return
        setInput('')
        const history = [...messages, { role: 'user', content: question }]
        setMessages(history)
        setLoading(true)

        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller
        const timer = setTimeout(() => controller.abort(), 60000)

        try {
            const res = await fetch(`${apiConfig.baseUrl}/ask`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
                body: JSON.stringify({
                    messages: history.map(({ role, content }) => ({ role, content })),
                    context: { page, combo, well },
                }),
            })
            const body = await res.json().catch(() => ({}))
            if (!res.ok) {
                const msg = res.status === 401 || res.status === 403
                    ? 'Please sign in with your Aviator Energy account to use Ask AI.'
                    : (body.error || `Request failed (${res.status}).`)
                setMessages(m => [...m, { role: 'assistant', content: msg, error: true }])
            } else {
                setMessages(m => [...m, {
                    role: 'assistant', content: body.answer,
                    toolCalls: body.toolCalls, data: body.data,
                }])
            }
        } catch {
            setMessages(m => [...m, {
                role: 'assistant', error: true,
                content: 'The request timed out or the connection dropped. The database may have been waking up — try again.',
            }])
        } finally {
            clearTimeout(timer)
            setLoading(false)
        }
    }

    const retry = () => {
        const lastUser = [...messages].reverse().find(m => m.role === 'user')
        if (!lastUser) return
        setMessages(m => {
            const idx = m.map(x => !!x.error).lastIndexOf(true)
            // drop the failed user turn + error bubble; send() re-adds the question
            return idx > 0 ? m.slice(0, idx - 1) : m
        })
        send(lastUser.content)
    }

    const suggestions = [
        'Last 30 vs last 60 day oil average on the Ramseur',
        'Top 10 oil wells this month',
        combo ? 'Downtime on this lease in the last 30 days' : 'Which wells had the most downtime last month?',
        'Company-wide production last 7 days',
    ]

    const panelBg = dark ? 'bg-slate-900 border-slate-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900'
    const headBg = dark ? 'bg-slate-800/60 border-slate-700' : 'bg-gray-50 border-gray-200'
    const aiBubble = dark ? 'bg-slate-800 border border-slate-700' : 'bg-gray-100 border border-gray-200'
    const chipCls = dark
        ? 'bg-slate-800 border-slate-600 hover:border-aviator-500 hover:bg-slate-700 text-gray-200'
        : 'bg-gray-50 border-gray-300 hover:border-aviator-500 hover:bg-gray-100 text-gray-700'

    return (
        <>
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    aria-label="Open Ask AI"
                    className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-gradient-to-br from-aviator-500 to-aviator-700 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-aviator-900/40 transition hover:-translate-y-0.5 hover:shadow-xl"
                >
                    <SparkleIcon className="h-5 w-5" />
                    <span className="hidden sm:inline">Ask AI</span>
                </button>
            )}

            {open && (
                <div className="fixed inset-0 z-40 bg-black/50 sm:bg-black/30" onClick={() => setOpen(false)} />
            )}

            <aside
                aria-label="Ask AI chat panel"
                className={`fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l shadow-2xl transition-transform duration-300 sm:w-[420px] ${panelBg} ${open ? 'translate-x-0' : 'translate-x-full'}`}
            >
                <div className={`flex items-center gap-3 border-b px-4 py-3 ${headBg}`}>
                    <div className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-gradient-to-br from-aviator-500 to-aviator-700 text-white">
                        <SparkleIcon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold">Ask AI</div>
                        {(page || combo || well) && (
                            <div className="mt-0.5 inline-flex max-w-full items-center truncate rounded border border-aviator-500/40 bg-aviator-500/10 px-1.5 py-px text-[11px] text-aviator-400">
                                {[page, well || combo].filter(Boolean).join(' · ')}
                            </div>
                        )}
                    </div>
                    <div className="ml-auto flex items-center gap-1">
                        {messages.length > 0 && (
                            <button
                                onClick={() => { setMessages([]); sessionStorage.removeItem(STORAGE_KEY) }}
                                className={`rounded px-2 py-1 text-xs ${dark ? 'text-gray-400 hover:bg-slate-700 hover:text-gray-200' : 'text-gray-500 hover:bg-gray-100'}`}
                            >Clear</button>
                        )}
                        <button
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                            className={`rounded px-2 py-1 text-sm ${dark ? 'text-gray-400 hover:bg-slate-700 hover:text-gray-200' : 'text-gray-500 hover:bg-gray-100'}`}
                        >✕</button>
                    </div>
                </div>

                <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-4">
                    {messages.length === 0 && !loading && (
                        <div className="mx-2 my-auto pt-8 text-center">
                            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-aviator-500 to-aviator-700 text-white">
                                <SparkleIcon className="h-6 w-6" />
                            </div>
                            <div className="text-sm font-semibold">Ask about your production data</div>
                            <p className={`mb-4 mt-1 text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Volumes, averages, comparisons, downtime — by well, lease, or company-wide.
                            </p>
                            <div className="space-y-2 text-left">
                                {suggestions.map(s => (
                                    <button key={s} onClick={() => send(s)}
                                        className={`block w-full rounded-lg border px-3 py-2 text-xs transition ${chipCls}`}>
                                        <span className="mr-1.5 text-aviator-400">→</span>{s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {messages.map((m, i) => (
                        <div key={i} className={`max-w-[88%] rounded-xl px-3 py-2 text-[13px] leading-relaxed ${
                            m.role === 'user'
                                ? 'ml-auto rounded-br-sm bg-aviator-600 text-white'
                                : `rounded-bl-sm ${aiBubble} ${m.error ? 'border-red-500/50 text-red-400' : ''}`
                        }`}>
                            {m.role === 'user' ? m.content : renderAnswer(m.content || '')}
                            {m.data && <DataTable data={m.data} dark={dark} />}
                            {m.toolCalls?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {m.toolCalls.map((t, j) => (
                                        <span key={j} className={`rounded border px-1.5 py-px font-mono text-[10px] ${dark ? 'border-slate-700 bg-slate-900 text-gray-500' : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
                                            {t.name} · {(t.ms / 1000).toFixed(1)}s
                                        </span>
                                    ))}
                                </div>
                            )}
                            {m.error && (
                                <button onClick={retry} className="mt-2 block rounded bg-aviator-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-aviator-500">
                                    Retry
                                </button>
                            )}
                        </div>
                    ))}

                    {loading && (
                        <div className={`flex w-16 items-center justify-center gap-1 rounded-xl rounded-bl-sm px-3 py-3 ${aiBubble}`}>
                            {[0, 1, 2].map(d => (
                                <span key={d} className="h-1.5 w-1.5 animate-pulse rounded-full bg-gray-400" style={{ animationDelay: `${d * 200}ms` }} />
                            ))}
                        </div>
                    )}
                </div>

                <div className={`border-t px-3 py-3 ${headBg}`}>
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') send() }}
                            placeholder="Ask about production, downtime, wells…"
                            className={`flex-1 rounded-lg border px-3 py-2 text-sm outline-none focus:border-aviator-500 ${dark ? 'border-slate-600 bg-slate-950 text-gray-100 placeholder-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'}`}
                        />
                        <button
                            onClick={() => send()}
                            disabled={loading || !input.trim()}
                            aria-label="Send"
                            className="grid w-10 flex-none place-items-center rounded-lg bg-aviator-600 text-white transition hover:bg-aviator-500 disabled:opacity-40"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                                <path d="M3.4 20.4l17.8-8.4L3.4 3.6 3.4 10l12 2-12 2z" />
                            </svg>
                        </button>
                    </div>
                    <p className={`mt-1.5 text-center text-[10px] ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Production data only · answers cite days-with-data when the window has gaps
                    </p>
                </div>
            </aside>
        </>
    )
}
